import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import * as FiIcons from 'react-icons/fi';

import SafeIcon from '../common/SafeIcon';
import { useClient } from '../context/ClientContext';
import { paymentService } from '../services/paymentService';

const {
  FiAlertCircle,
  FiAlertTriangle,
  FiArrowDownLeft,
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiDownload,
  FiFileText,
  FiFilter,
  FiLoader,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiX
} = FiIcons;

const tabs = ['All', 'Completed', 'Pending', 'Refunded'];

const methods = [
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'other', label: 'Other' }
];

const today = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const formatMoney = (minorUnits, currency = 'USD') => {
  const amount = Number(minorUnits || 0) / 100;

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
};

const formatDate = (date) => {
  if (!date) return '—';

  try {
    return format(new Date(`${date.slice(0, 10)}T00:00:00`), 'MMM dd, yyyy');
  } catch {
    return date;
  }
};

const formatMethod = (method) => {
  const names = {
    bank_transfer: 'Bank Transfer',
    cash: 'Cash',
    check: 'Check',
    card: 'Card',
    link: 'Link',
    other: 'Other'
  };

  return names[method] ||
    String(method || 'Other').replace(/_/g, ' ');
};

const statusClass = (status) => {
  const value = String(status || '').toLowerCase();

  if (value === 'completed') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  }

  if (value === 'pending') {
    return 'border-amber-200 bg-amber-50 text-amber-700';
  }

  if (value.includes('refund')) {
    return 'border-violet-200 bg-violet-50 text-violet-700';
  }

  return 'border-slate-200 bg-slate-50 text-slate-600';
};

const csvSafe = (value) => {
  let text = String(value ?? '');

  if (/^[\s]*[=+\-@]/.test(text)) {
    text = `'${text}`;
  }

  return `"${text.replace(/"/g, '""')}"`;
};

function StatusBadge({ status }) {
  return (
    <span
      className={`inline-flex rounded-md border px-2 py-1 text-[10px] font-semibold uppercase tracking-wide ${statusClass(status)}`}
    >
      {String(status || 'unknown').replace(/_/g, ' ')}
    </span>
  );
}

function StatCard({ icon, label, value, subtitle, tone = 'blue' }) {
  const iconColor = {
    blue: 'text-blue-500',
    emerald: 'text-emerald-500',
    amber: 'text-amber-500',
    slate: 'text-slate-500'
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
      <div className="flex items-center gap-2">
        <SafeIcon
          icon={icon}
          className={`text-sm ${iconColor[tone]}`}
        />
        <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">
          {label}
        </span>
      </div>

      <p className="mt-2 text-lg font-medium tracking-tight text-slate-900">
        {value}
      </p>

      <p className="mt-1 text-[11px] text-slate-400">
        {subtitle}
      </p>
    </div>
  );
}

function Modal({ title, subtitle, onClose, children, busy }) {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !busy) {
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [busy, onClose]);

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/40 px-4 py-6 backdrop-blur-[2px]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) {
          onClose();
        }
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 8, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              {title}
            </h2>
            <p className="mt-1 text-xs leading-5 text-slate-500">
              {subtitle}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="ml-4 flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 disabled:opacity-50"
          >
            <SafeIcon icon={FiX} />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5">
          {children}
        </div>
      </motion.div>
    </div>
  );
}

const initialForm = () => ({
  customerId: '',
  invoiceId: '',
  amount: '',
  paymentDate: today(),
  paymentMethod: 'bank_transfer',
  referenceNumber: '',
  notes: ''
});

export default function PaymentsReceived() {
  const { activeClient } = useClient();

  const organizationId = activeClient?.id;

  const defaultCurrency = String(
    activeClient?.currency ||
    activeClient?.billingCurrency ||
    'USD'
  ).toUpperCase();

  const [payments, setPayments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [eligibleInvoices, setEligibleInvoices] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [activeTab, setActiveTab] = useState('All');
  const [search, setSearch] = useState('');
  const [methodFilter, setMethodFilter] = useState('all');

  const [showCreate, setShowCreate] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const [form, setForm] = useState(initialForm);
  const savingRef = useRef(false);
  const organizationRef = useRef(organizationId);
  organizationRef.current = organizationId;

  const loadData = useCallback(async (silent = false) => {
    const requestedOrganizationId = organizationId;

    if (!requestedOrganizationId) {
      setPayments([]);
      setCustomers([]);
      setEligibleInvoices([]);
      setLoading(false);
      return;
    }

    if (silent) setRefreshing(true);
    else setLoading(true);

    setError('');

    try {
      const [paymentData, customerData, invoiceData] =
        await Promise.all([
          paymentService.getPayments(requestedOrganizationId),
          paymentService.getCustomers(requestedOrganizationId),
          paymentService.getEligibleInvoices(requestedOrganizationId)
        ]);

      if (organizationRef.current !== requestedOrganizationId) {
        return;
      }

      setPayments(paymentData);
      setCustomers(customerData);
      setEligibleInvoices(invoiceData);
    } catch (err) {
      if (organizationRef.current !== requestedOrganizationId) {
        return;
      }

      console.error('[Payments Received] Load failed:', err);
      setError(err.message || 'Unable to load payments.');
    } finally {
      if (organizationRef.current === requestedOrganizationId) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [organizationId]);

  useEffect(() => {
    setPayments([]);
    setCustomers([]);
    setEligibleInvoices([]);
    setShowCreate(false);
    setSelectedPayment(null);
    setForm(initialForm());
    setLoading(true);
    setSuccess('');
    loadData();
  }, [loadData]);

  const customerInvoices = useMemo(
    () => eligibleInvoices.filter(
      (invoice) => invoice.customer_id === form.customerId
    ),
    [eligibleInvoices, form.customerId]
  );

  const selectedInvoice = customerInvoices.find(
    (invoice) => invoice.id === form.invoiceId
  );

  const selectedCustomer = customers.find(
    (customer) => customer.id === form.customerId
  );

  const stats = useMemo(() => {
    const completed = payments.filter(
      (payment) => payment.status === 'completed'
    );

    const totals = {};

    for (const payment of completed) {
      const currency = payment.currency || defaultCurrency;
      totals[currency] = (totals[currency] || 0) +
        Number(payment.amount || 0);
    }

    const month = today().slice(0, 7);

    const thisMonth = completed.filter(
      (payment) => String(payment.payment_date).startsWith(month)
    );

    const monthTotals = {};

    for (const payment of thisMonth) {
      const currency = payment.currency || defaultCurrency;
      monthTotals[currency] = (monthTotals[currency] || 0) +
        Number(payment.amount || 0);
    }

    return {
      completed: completed.length,
      pending: payments.filter((p) => p.status === 'pending').length,
      totals,
      monthTotals
    };
  }, [payments, defaultCurrency]);

  const displayTotals = (totals) => {
    const entries = Object.entries(totals);

    if (!entries.length) return formatMoney(0, defaultCurrency);

    return entries.map(([currency, amount]) =>
      formatMoney(amount, currency)
    ).join(' · ');
  };

  const filteredPayments = useMemo(() => {
    const query = search.trim().toLowerCase();

    return payments.filter((payment) => {
      const status = String(payment.status || '').toLowerCase();

      if (
        activeTab === 'Completed' &&
        status !== 'completed'
      ) return false;

      if (
        activeTab === 'Pending' &&
        status !== 'pending'
      ) return false;

      if (
        activeTab === 'Refunded' &&
        !status.includes('refund')
      ) return false;

      if (
        methodFilter !== 'all' &&
        payment.payment_method !== methodFilter
      ) return false;

      if (!query) return true;

      const fields = [
        payment.payment_number,
        payment.reference_number,
        payment.customer?.name,
        payment.customer?.company_name,
        payment.customer?.email,
        payment.payment_method,
        ...payment.allocations.map(
          (a) => a.invoice?.invoice_number
        )
      ];

      return fields.some(
        (field) => String(field || '').toLowerCase().includes(query)
      );
    });
  }, [payments, activeTab, methodFilter, search]);

  const exportCSV = () => {
    if (!filteredPayments.length) return;

    const headers = [
      'Payment Date',
      'Payment Number',
      'Customer',
      'Invoices',
      'Source',
      'Method',
      'Reference',
      'Currency',
      'Amount (Minor Units)',
      'Status'
    ];

    const rows = filteredPayments.map((payment) => [
      payment.payment_date,
      payment.payment_number || payment.id,
      payment.customer?.name || '',
      payment.allocations.map(
        (a) => a.invoice?.invoice_number || a.invoice_id
      ).join('; '),
      payment.payment_source,
      payment.payment_method,
      payment.reference_number || '',
      payment.currency,
      payment.amount,
      payment.status
    ]);

    const csv = [
      headers.map(csvSafe).join(','),
      ...rows.map((row) => row.map(csvSafe).join(','))
    ].join('\r\n');

    const blob = new Blob(
      ['\uFEFF', csv],
      { type: 'text/csv;charset=utf-8;' }
    );

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `payments-received-${today()}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(url);
  };

  const handleSave = async (event) => {
    event.preventDefault();

    if (savingRef.current || !organizationId) return;

    if (!selectedInvoice) {
      setError('Select an eligible invoice.');
      return;
    }

    let amount;

    try {
      amount = paymentService.toMinorUnits(form.amount);
    } catch (err) {
      setError(err.message);
      return;
    }

    if (amount > Number(selectedInvoice.amount_due)) {
      setError('Payment exceeds the invoice balance.');
      return;
    }

    const requestedOrganizationId = organizationId;

    savingRef.current = true;
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await paymentService.recordManualPayment(
        requestedOrganizationId,
        form
      );

      if (organizationRef.current !== requestedOrganizationId) {
        return;
      }

      setShowCreate(false);
      setForm(initialForm());
      setSuccess('Payment recorded successfully.');

      await loadData(true);
    } catch (err) {
      if (organizationRef.current === requestedOrganizationId) {
        setError(err.message || 'Unable to record payment.');
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const fieldClass =
    'w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-700 outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-100';

  const labelClass =
    'mb-1.5 block text-[11px] font-semibold text-slate-600';

  if (!organizationId) {
    return (
      <div className="mx-auto max-w-[1500px]">
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-5">
          <SafeIcon icon={FiAlertTriangle} className="text-amber-600" />
          <div>
            <p className="text-sm font-semibold text-amber-900">
              No organization selected
            </p>
            <p className="mt-1 text-xs text-amber-700">
              Select a client organization to view payments.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1500px] pb-8">
      <div className="mb-5 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-900">
            Payments Received
          </h1>

          <p className="mt-1 text-xs text-slate-500">
            Track customer payments for{' '}
            <span className="font-medium text-slate-700">
              {activeClient?.name || 'this organization'}
            </span>.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => loadData(true)}
            disabled={refreshing || saving}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-xs font-medium text-slate-600 shadow-sm hover:bg-slate-50 disabled:opacity-50"
          >
            <SafeIcon
              icon={refreshing ? FiLoader : FiRefreshCw}
              className={refreshing ? 'animate-spin' : ''}
            />
            Refresh
          </button>

          <button
            type="button"
            onClick={() => {
              setError('');
              setShowCreate(true);
            }}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-xs font-medium text-white shadow-sm hover:bg-slate-800 disabled:opacity-50"
          >
            <SafeIcon icon={FiPlus} />
            Record Payment
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
          <SafeIcon icon={FiAlertTriangle} className="mt-0.5" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError('')}>
            <SafeIcon icon={FiX} />
          </button>
        </div>
      )}

      {success && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-700">
          <SafeIcon icon={FiCheckCircle} />
          <span className="flex-1">{success}</span>
          <button type="button" onClick={() => setSuccess('')}>
            <SafeIcon icon={FiX} />
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex min-h-[300px] flex-col items-center justify-center rounded-xl border border-slate-200 bg-white">
          <SafeIcon
            icon={FiLoader}
            className="mb-2 animate-spin text-2xl text-blue-600"
          />
          <p className="text-xs text-slate-500">
            Loading payments...
          </p>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-5"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              icon={FiArrowDownLeft}
              label="Total Received"
              value={displayTotals(stats.totals)}
              subtitle="Completed ledger payments"
              tone="emerald"
            />

            <StatCard
              icon={FiCreditCard}
              label="This Month"
              value={displayTotals(stats.monthTotals)}
              subtitle="Completed payments this month"
              tone="blue"
            />

            <StatCard
              icon={FiCheckCircle}
              label="Completed"
              value={stats.completed}
              subtitle="Recorded transactions"
              tone="slate"
            />

            <StatCard
              icon={FiClock}
              label="Pending"
              value={stats.pending}
              subtitle="Awaiting completion"
              tone="amber"
            />
          </div>

          <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex max-w-full gap-1 overflow-x-auto rounded-lg bg-slate-50 p-1">
                {tabs.map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    className={`whitespace-nowrap rounded-md px-3 py-1.5 text-[11px] font-medium transition ${
                      activeTab === tab
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-800'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="relative min-w-[180px] flex-1">
                  <SafeIcon
                    icon={FiSearch}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400"
                  />
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search payments..."
                    className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-xs outline-none focus:border-slate-400"
                  />
                </div>

                <div className="relative">
                  <SafeIcon
                    icon={FiFilter}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <select
                    value={methodFilter}
                    onChange={(event) => setMethodFilter(event.target.value)}
                    className="rounded-lg border border-slate-200 bg-white py-2 pl-8 pr-3 text-xs text-slate-700"
                  >
                    <option value="all">All methods</option>
                    {methods.map((method) => (
                      <option key={method.value} value={method.value}>
                        {method.label}
                      </option>
                    ))}
                    <option value="link">Link</option>
                    <option value="card">Card</option>
                  </select>
                </div>

                <button
                  type="button"
                  onClick={exportCSV}
                  disabled={!filteredPayments.length}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                >
                  <SafeIcon icon={FiDownload} />
                  Export
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-left">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">
                    <th className="px-5 py-3">Date</th>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">Invoice</th>
                    <th className="px-4 py-3">Method</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Amount</th>
                    <th className="px-5 py-3 text-right">Action</th>
                  </tr>
                </thead>

                <tbody className="text-xs">
                  {!filteredPayments.length ? (
                    <tr>
                      <td colSpan="7" className="px-5 py-16 text-center">
                        <SafeIcon
                          icon={FiFileText}
                          className="mx-auto mb-3 text-3xl text-slate-300"
                        />
                        <p className="text-sm font-medium text-slate-800">
                          No payments found
                        </p>
                        <p className="mt-1 text-xs text-slate-400">
                          {payments.length
                            ? 'Try changing your search or filters.'
                            : 'Recorded payments will appear here.'}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    filteredPayments.map((payment) => (
                      <tr
                        key={payment.id}
                        className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/70"
                      >
                        <td className="whitespace-nowrap px-5 py-4 text-slate-600">
                          {formatDate(payment.payment_date)}
                        </td>

                        <td className="px-4 py-4">
                          <p className="font-medium text-slate-800">
                            {payment.customer?.name || 'Customer'}
                          </p>
                          <p className="mt-1 text-[11px] text-slate-400">
                            {payment.reference_number ||
                              payment.payment_number ||
                              'No reference'}
                          </p>
                        </td>

                        <td className="px-4 py-4 text-slate-600">
                          {payment.allocations.length
                            ? payment.allocations.map(
                                (a) =>
                                  a.invoice?.invoice_number ||
                                  'Invoice'
                              ).join(', ')
                            : 'Unapplied'}
                        </td>

                        <td className="px-4 py-4">
                          <p className="text-slate-700">
                            {formatMethod(payment.payment_method)}
                          </p>
                          <p className="mt-1 text-[10px] capitalize text-slate-400">
                            {payment.payment_source}
                          </p>
                        </td>

                        <td className="px-4 py-4">
                          <StatusBadge status={payment.status} />
                        </td>

                        <td className="whitespace-nowrap px-4 py-4 text-right font-semibold text-slate-900">
                          {formatMoney(payment.amount, payment.currency)}
                        </td>

                        <td className="px-5 py-4 text-right">
                          <button
                            type="button"
                            onClick={() => setSelectedPayment(payment)}
                            className="rounded-lg border border-slate-200 px-3 py-1.5 text-[11px] font-medium text-slate-600 hover:bg-slate-50"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="border-t border-slate-200 bg-slate-50/60 px-5 py-3 text-[11px] text-slate-500">
              Showing {filteredPayments.length} of {payments.length} payments
            </div>
          </section>
        </motion.div>
      )}

      {showCreate && (
        <Modal
          title="Record Payment"
          subtitle="Apply a manual payment to an eligible invoice."
          onClose={() => {
            if (!saving) setShowCreate(false);
          }}
          busy={saving}
        >
          <form onSubmit={handleSave} className="space-y-4">
            <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-3 text-[11px] leading-5 text-blue-700">
              Only non-Stripe invoices are eligible. Stripe payments
              are recorded automatically through payment synchronization.
            </div>

            <div>
              <label className={labelClass}>Customer *</label>
              <select
                className={fieldClass}
                value={form.customerId}
                required
                disabled={saving}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    customerId: event.target.value,
                    invoiceId: '',
                    amount: ''
                  }))
                }
              >
                <option value="">Select customer</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Invoice *</label>
              <select
                className={fieldClass}
                value={form.invoiceId}
                required
                disabled={!form.customerId || saving}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    invoiceId: event.target.value,
                    amount: ''
                  }))
                }
              >
                <option value="">Select unpaid invoice</option>
                {customerInvoices.map((invoice) => (
                  <option key={invoice.id} value={invoice.id}>
                    {invoice.invoice_number} —{' '}
                    {formatMoney(invoice.amount_due, invoice.currency)}
                  </option>
                ))}
              </select>

              {form.customerId && !customerInvoices.length && (
                <p className="mt-2 text-[11px] text-amber-600">
                  No eligible manual-payment invoices for this customer.
                </p>
              )}
            </div>

            {selectedInvoice && (
              <div className="rounded-lg bg-slate-50 p-3">
                <p className="text-[10px] font-semibold uppercase text-slate-400">
                  Outstanding Balance
                </p>
                <p className="mt-1 text-base font-semibold text-slate-900">
                  {formatMoney(
                    selectedInvoice.amount_due,
                    selectedInvoice.currency
                  )}
                </p>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() =>
                    setForm((current) => ({
                      ...current,
                      amount: (
                        Number(selectedInvoice.amount_due) / 100
                      ).toFixed(2)
                    }))
                  }
                  className="mt-2 text-[11px] font-medium text-blue-600 hover:underline"
                >
                  Use full balance
                </button>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>
                  Amount ({selectedInvoice?.currency || defaultCurrency}) *
                </label>
                <input
                  className={fieldClass}
                  type="number"
                  min="0.01"
                  step="0.01"
                  required
                  disabled={!selectedInvoice || saving}
                  value={form.amount}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      amount: event.target.value
                    }))
                  }
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className={labelClass}>Payment Date *</label>
                <input
                  className={fieldClass}
                  type="date"
                  required
                  disabled={saving}
                  value={form.paymentDate}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      paymentDate: event.target.value
                    }))
                  }
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>Payment Method *</label>
              <select
                className={fieldClass}
                value={form.paymentMethod}
                disabled={saving}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    paymentMethod: event.target.value
                  }))
                }
              >
                {methods.map((method) => (
                  <option key={method.value} value={method.value}>
                    {method.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Reference Number</label>
              <input
                className={fieldClass}
                value={form.referenceNumber}
                disabled={saving}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    referenceNumber: event.target.value
                  }))
                }
                placeholder="Bank reference or check number"
              />
            </div>

            <div>
              <label className={labelClass}>Notes</label>
              <textarea
                className={`${fieldClass} min-h-[80px] resize-y`}
                value={form.notes}
                disabled={saving}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    notes: event.target.value
                  }))
                }
                placeholder="Optional payment notes"
              />
            </div>

            {selectedCustomer && (
              <p className="text-[11px] text-slate-400">
                Payment will be recorded for {selectedCustomer.name}.
              </p>
            )}

            <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
              <button
                type="button"
                disabled={saving}
                onClick={() => setShowCreate(false)}
                className="rounded-lg border border-slate-200 px-4 py-2.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={saving || !selectedInvoice}
                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
              >
                <SafeIcon
                  icon={saving ? FiLoader : FiCheckCircle}
                  className={saving ? 'animate-spin' : ''}
                />
                {saving ? 'Recording...' : 'Record Payment'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {selectedPayment && (
        <Modal
          title="Payment Details"
          subtitle="Recorded payment information and invoice allocations."
          onClose={() => setSelectedPayment(null)}
        >
          <div className="space-y-4 text-xs">
            <div className="rounded-xl bg-slate-50 p-4">
              <p className="text-[10px] font-semibold uppercase text-slate-400">
                Amount Received
              </p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">
                {formatMoney(
                  selectedPayment.amount,
                  selectedPayment.currency
                )}
              </p>
              <div className="mt-3">
                <StatusBadge status={selectedPayment.status} />
              </div>
            </div>

            {[
              ['Customer', selectedPayment.customer?.name || '—'],
              ['Date', formatDate(selectedPayment.payment_date)],
              ['Method', formatMethod(selectedPayment.payment_method)],
              ['Source', selectedPayment.payment_source],
              ['Reference', selectedPayment.reference_number || '—'],
              ['Notes', selectedPayment.notes || '—']
            ].map(([label, value]) => (
              <div
                key={label}
                className="flex justify-between gap-4 border-b border-slate-100 pb-3"
              >
                <span className="text-slate-400">{label}</span>
                <span className="max-w-[65%] break-words text-right font-medium text-slate-800">
                  {value}
                </span>
              </div>
            ))}

            <div>
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Applied to Invoices
              </p>

              {selectedPayment.allocations.length ? (
                selectedPayment.allocations.map((allocation) => (
                  <div
                    key={allocation.id}
                    className="mb-2 flex justify-between gap-3 rounded-lg border border-slate-200 p-3"
                  >
                    <span className="font-medium text-slate-700">
                      {allocation.invoice?.invoice_number || 'Invoice'}
                    </span>
                    <span className="font-semibold text-slate-900">
                      {formatMoney(
                        allocation.amount,
                        selectedPayment.currency
                      )}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-slate-400">
                  No invoice allocation recorded.
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={() => setSelectedPayment(null)}
              className="w-full rounded-lg bg-slate-900 px-4 py-2.5 text-xs font-medium text-white hover:bg-slate-800"
            >
              Close
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}